import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Box, CornerDownLeft, Tag, User, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useCreateCommitment } from '@/features/commitments';
import { useCreateElement, useCreateRelation, useSetOwners } from '@/features/elements';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Mark, useToastStore } from '@/shared/ui';

import { useOmniboxStore } from '../model/omniboxStore';
import { nextIndex, optionDomId, selectableOf } from '../model/options';
import type { OmniboxRow } from '../model/options';
import {
  allResolved,
  blamedOwner,
  candidateKey,
  resolvedElementIds,
  resolvedOwners,
  resolvedTypeName,
  withdrawnTypeName,
} from '../model/references';
import type {
  ElementTypeEntry,
  Reference,
  ReferenceCandidate,
  ResolvedOwner,
} from '../model/references';
import { isCreation, PREFIXES } from '../model/types';
import type { ElementSearchHit, ParsedCommand } from '../model/types';
import { useReferences } from '../queries/useReferences';
import { useOmnibox } from '../queries/useSearch';
import { CommandMirror } from './CommandMirror';
import styles from './Omnibox.module.css';
import { OmniboxDialog } from './OmniboxDialog';
import { OmniboxList } from './OmniboxList';
import { OmniboxSky } from './OmniboxSky';

/**
 * Omnibox — jedno pole na każdej stronie (spec sek. 5, decyzje 54–55).
 *
 * **Wysokość jednego wiersza, nie mega-wyszukiwarka** (decyzja 55): charakter niesie
 * materiał, a nie rozmiar.
 *
 * ⚠ Powierzchnia **przestała być ciemna niezależnie od motywu** (2026-08-06): kompromis
 * między dwoma tłami w jasnym krzyczał, a w ciemnym znikał. Dziś okno jest tym samym
 * materiałem, co wyzwalacz w pasku (`OmniboxTrigger`) — patrz `Omnibox.module.css`.
 *
 * ⚠ **Lista jest własna, nie z `cmdk`** — i to rozstrzygnięcie, nie oszczędność.
 * `cmdk` identyfikuje zaznaczenie **wartością** pozycji i przywraca je automatycznie tylko
 * wtedy, gdy sam filtruje listę. Nasze szukanie jest **serwerowe** (fuzzy, ścięte ogonki),
 * więc filtr musiał być wyłączony — a wtedy po zmianie frazy, gdy zaznaczony element
 * wypadał z wyników, zaznaczenie wskazywało donikąd i **znikało**; Enter nie miał czego
 * wykonać. To nie był błąd biblioteki, tylko niezgodność modelu: `cmdk` zakłada statyczną
 * listę filtrowaną lokalnie.
 *
 * Okno też jest własne (`OmniboxDialog`) — patrz tam po uzasadnienie: w tym oknie stoi
 * dokładnie jeden element łapiący ognisko, więc pułapka ogniska z gotowej biblioteki
 * rozwiązuje problem, którego nie mamy.
 */
const LIST_ID = 'omnibox-list';

export function Omnibox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const describeError = useApiErrorMessage();

  const isOpen = useOmniboxStore((s) => s.isOpen);
  const initialInput = useOmniboxStore((s) => s.initialInput);
  const open = useOmniboxStore((s) => s.open);
  const close = useOmniboxStore((s) => s.close);

  const [input, setInput] = useState('');
  // Wybór człowieka trzymamy jako CAŁEGO kandydata, nie identyfikator: byt organizacyjny
  // niesie jeszcze rodzaj (user/zespół), a bez niego nie dałoby się go zapisać.
  const [picked, setPicked] = useState<Record<string, ReferenceCandidate>>({});
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const createElement = useCreateElement();
  const createRelation = useCreateRelation();
  const createCommitment = useCreateCommitment();
  const setOwners = useSetOwners();
  const showToast = useToastStore((s) => s.show);

  // Skrót globalny. `Ctrl/Cmd+K` to konwencja, której ludzie nauczyli się gdzie indziej.
  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();

        if (useOmniboxStore.getState().isOpen) {
          close();
        } else {
          open();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (isOpen) {
      setInput(initialInput);
      // Okno właśnie się otworzyło, więc porwanie ogniska jest tu poprawne —
      // inaczej człowiek musiałby kliknąć w pole, żeby zacząć pisać.
      inputRef.current?.focus();
    }
  }, [isOpen, initialInput]);

  // ⚠ Zmiana tekstu kasuje wybory. Bez tego wskazanie „Eksport CSV" dla jednej komendy
  // przykleiłoby się do zupełnie innej, wpisanej chwilę później.
  useEffect(() => {
    setPicked({});
  }, [input]);

  const { data, isFetching, isStale, error } = useOmnibox(input, isOpen);
  const command = data?.command;
  const hits = data?.hits ?? [];

  const { references, candidatesByKey, resolutions, typeDictionary } = useReferences(
    command,
    isOpen,
  );

  // Którą nazwę człowiek WŁAŚNIE pisze — ostatnią, i tylko wtedy, gdy naprawdę stoi
  // na końcu pola.
  //
  // ⚠ Sam „ostatnia referencja, dopóki nie ma spacji" przestał wystarczać, gdy typ stał się
  // referencją: w `+@moduł Bramka` ostatnią (i jedyną) referencją jest typ, a pisany jest
  // **tytuł**. Bez sprawdzenia zakończenia podpowiedzi typu wisiałyby przez całe pisanie
  // reszty komendy.
  const lastReference = references.at(-1);
  const beingTyped =
    lastReference && !input.endsWith(' ') && input.endsWith(lastReference.name)
      ? lastReference
      : undefined;

  const settled = resolutions.map((resolution) => {
    const pickedCandidate = picked[resolution.key];

    return pickedCandidate === undefined
      ? resolution
      : { key: resolution.key, state: 'resolved' as const, candidate: pickedCandidate };
  });

  const readyToRun = allResolved(settled);

  function goTo(elementId: number) {
    close();
    void navigate(`/element/${elementId}`);
  }

  /** Wstawia pełną nazwę w miejsce pisanego fragmentu — człowiek widzi, co wybrał. */
  function pickReference(referenceKey: string, candidate: ReferenceCandidate) {
    const reference = references.find((r) => r.key === referenceKey);

    if (reference) {
      const start = input.lastIndexOf(reference.name);

      if (start >= 0) {
        setInput(
          input.slice(0, start) + candidate.title + input.slice(start + reference.name.length),
        );
      }
    }

    // `setInput` czyści wybory osobnym efektem, więc wybór zapisujemy PO nim.
    setTimeout(() => setPicked((previous) => ({ ...previous, [referenceKey]: candidate })), 0);
  }

  /**
   * Przypisuje właścicieli **jednym żądaniem, niepodzielnie** (decyzja 97).
   *
   * ⚠ Do 2026-08-06 szło to pętlą, po jednym żądaniu na nazwę — i nie z lenistwa: kolejność
   * niosła informację, **którego** właściciela zabrakło. Ceną było częściowe przypisanie
   * (dwóch weszło, trzeci nie), a odmowa mówiła o stanie, którego nikt nie widział.
   *
   * ⭐ Wsad utrzymuje jedno i kasuje drugie: serwer odrzuca całość i nazywa w `args`
   * **pierwszego** brakującego (`kind` + `id`), a nazwę dokłada wołający z listy, którą sam
   * zbudował — nic nie trzeba dopytywać (decyzja 96).
   *
   * Zwraca winowajcę, gdy odmowa dotyczy konkretnego właściciela, i `null` w miejscu nazwy,
   * gdy dotyczy czegoś innego (np. braku prawa do edycji elementu) — wtedy wskazywanie
   * którejkolwiek nazwy byłoby wprowadzaniem w błąd.
   */
  async function assignOwners(
    elementId: number,
    owners: readonly ResolvedOwner[],
    inherit: boolean,
  ): Promise<{ owner: ResolvedOwner | null; failure: unknown } | null> {
    if (owners.length === 0) {
      return null;
    }

    try {
      await setOwners.mutateAsync({
        elementId,
        owners: owners.map((owner) => ({ ownerKind: owner.ownerKind, ownerId: owner.ownerId })),
        inherit,
      });

      return null;
    } catch (failure) {
      return { owner: blamedOwner(owners, failure), failure };
    }
  }

  async function run(commandToRun: ParsedCommand) {
    if (!commandToRun.isComplete) {
      return;
    }

    if (!readyToRun) {
      showToast({ message: t('search.pickFirst'), tone: 'neutral' });
      return;
    }

    const id = resolvedElementIds(settled);
    const owners = resolvedOwners(settled);

    // `@inherit:nie` wyłącza spływanie własności w dół po `has` (spec 6.5). Brak modyfikatora
    // znaczy „dziedzicz" — tak mówi spec 9.1 i tak zachowuje się serwer, gdy pola nie ma.
    const inherit = commandToRun.inherit ?? true;

    try {
      if (commandToRun.kind === 'createElement' && commandToRun.title) {
        const created = await createElement.mutateAsync({
          title: commandToRun.title,
          // ⭐ Nazwa KANONICZNA ze słownika, nie to, co wpisano: `+@modul …` zapisuje
          // „Moduł". Serwer i tak kanonizuje, więc wysłanie surowego tekstu dałoby ten sam
          // wiersz w bazie — ale wtedy lustro pokazywałoby przed zapisem co innego, niż
          // po zapisie widać na elemencie.
          type: resolvedTypeName(settled),
          description: commandToRun.description,
        });

        // Rodzic z `@in` to zwykła krawędź `has` — element powstaje najpierw, bo dopiero
        // wtedy ma identyfikator.
        const parentId = id.get('parent');

        if (parentId !== undefined) {
          await createRelation.mutateAsync({
            fromElementId: parentId,
            toElementId: created.id,
            type: 'has',
          });
        }

        // ⚠ Właściciel z tego samego powodu dokładany jest PO utworzeniu. Gdy przypisanie
        // polegnie, element już istnieje — mówimy o tym wprost (nazywając właściciela,
        // którego zabrakło) i i tak prowadzimy na ekran elementu, bo to jedyne miejsce,
        // w którym da się to dokończyć ręcznie. Milczenie byłoby tu cichą stratą.
        const failed = await assignOwners(created.id, owners, inherit);

        showToast(
          failed
            ? {
                // Winowajca z nazwą, gdy serwer go wskazał; bez nazwy, gdy odmowa dotyczyła
                // czegoś innego — wtedy wpisanie tu pierwszej nazwy z listy byłoby zmyślaniem.
                message: failed.owner
                  ? t('search.ownerFailedAfterCreate', {
                      title: created.title,
                      name: failed.owner.title,
                      reason: describeError(failed.failure),
                    })
                  : t('search.ownersFailedAfterCreate', {
                      title: created.title,
                      reason: describeError(failed.failure),
                    }),
                tone: 'danger',
              }
            : { message: t('search.created', { title: created.title }), tone: 'success' },
        );

        goTo(created.id);
        return;
      }

      if (commandToRun.kind === 'createRelation') {
        const subject = id.get('subject');

        if (subject === undefined) {
          return;
        }

        // ⭐ `>Faktura @owner QA` to PRZYPISANIE WŁAŚCICIELA, a nie krawędź grafu (spec 6.5).
        // ⚠ Bez tego rozgałęzienia komenda wpadała niżej i — skoro `owner` nie jest `has` —
        // tworzyła krawędź `depends-on` do elementu o nazwie „QA". Jeśli taki element
        // istniał, graf dostawał fałszywą zależność bez jednego słowa ostrzeżenia.
        if (commandToRun.relation === 'owner') {
          const failed = await assignOwners(subject, owners, inherit);

          showToast(
            failed
              ? {
                  message: failed.owner
                    ? t('search.ownerFailed', {
                        name: failed.owner.title,
                        reason: describeError(failed.failure),
                      })
                    : t('search.ownersFailed', { reason: describeError(failed.failure) }),
                  tone: 'danger',
                }
              : { message: t('search.ownerSet', { count: owners.length }), tone: 'success' },
          );

          goTo(subject);
          return;
        }

        const relationType = commandToRun.relation === 'has' ? 'has' : 'dependsOn';

        // ⚠ `reversed` zamienia KOŃCE, a nie typ relacji: `>A @used-by B, C` to dwie
        // krawędzie prowadzące DO A (decyzja 46 — w bazie są dwa typy, nie cztery).
        //
        // ⚠ SEKWENCYJNIE: serwer sprawdza cykle w `has` przed zapisem, a dwa równoległe
        // żądania mogłyby przejść tę kontrolę osobno i wspólnie zamknąć pętlę.
        for (const [index] of commandToRun.targets.entries()) {
          const targetId = id.get(`target-${index}`);

          if (targetId === undefined) {
            continue;
          }

          // eslint-disable-next-line no-await-in-loop -- patrz wyżej: kolejność jest wymogiem
          await createRelation.mutateAsync({
            fromElementId: commandToRun.reversed ? targetId : subject,
            toElementId: commandToRun.reversed ? subject : targetId,
            type: relationType,
          });
        }

        showToast({
          message: t('search.relationCreated', { count: commandToRun.targets.length }),
          tone: 'success',
        });
        goTo(subject);
        return;
      }

      if (commandToRun.kind === 'createCommitment' && commandToRun.title) {
        const hostId = id.get('subject');

        if (hostId === undefined) {
          return;
        }

        await createCommitment.mutateAsync({
          hostElementId: hostId,
          title: commandToRun.title,
          body: commandToRun.description,
          iterationNext: commandToRun.iterationNext,
          iterationNumber: commandToRun.iterationNumber,
        });

        showToast({ message: t('search.commitmentCreated'), tone: 'success' });
        goTo(hostId);
      }
    } catch (failure) {
      showToast({ message: describeError(failure), tone: 'danger' });
    }
  }

  // ── Budowa listy ───────────────────────────────────────────────────────────
  //
  // Jedna płaska lista wierszy, budowana od nowa przy każdym renderze. To ona jest
  // źródłem prawdy dla nawigacji klawiaturą — nie ma osobnego rejestru pozycji,
  // który mógłby się z nią rozjechać. Dokładnie ten rozjazd był chorobą `cmdk`.

  const rows: OmniboxRow[] = [];

  if (input.trim() === '') {
    rows.push({ kind: 'heading', key: 'h-prefixes', label: t('search.prefixes') });

    for (const p of PREFIXES) {
      if (p.prefix !== '') {
        rows.push({
          kind: 'option',
          key: `prefix-${p.prefix}`,
          onSelect: () => setInput(p.prefix),
        });
      }
    }
  }

  if (command && isCreation(command.kind)) {
    rows.push({
      kind: 'option',
      key: 'run',
      disabled: !command.isComplete,
      onSelect: () => void run(command),
    });
  }

  // Kandydaci pod kluczem WIERSZA. Bez tej mapy `renderRow` musiałby rozbierać klucz na
  // kawałki, żeby odnaleźć, co pokazuje — a klucz niesie teraz byty z dwóch różnych
  // katalogów, więc rozbieranie go po myślniku byłoby zgadywaniem.
  const candidateByRow = new Map<string, ReferenceCandidate>();

  references.forEach((reference, index) => {
    const candidates = candidatesByKey.get(reference.key) ?? [];

    // Podpowiadamy przy nazwie WŁAŚNIE PISANEJ (nawet gdy już się rozstrzygnęła — człowiek
    // ma widzieć, co dopasowano, i móc to zmienić) oraz wszędzie, gdzie wybór jest konieczny.
    const shouldShow =
      reference.key === beingTyped?.key || settled[index]?.state === 'ambiguous';

    if (!shouldShow || candidates.length === 0) {
      return;
    }

    rows.push({
      kind: 'heading',
      key: `h-${reference.key}`,
      label: t(`search.pick.${reference.role}`, { name: reference.name }),
    });

    for (const candidate of candidates) {
      const rowKey = `pick-${reference.key}-${candidateKey(candidate)}`;

      candidateByRow.set(rowKey, candidate);

      rows.push({
        kind: 'option',
        key: rowKey,
        onSelect: () => pickReference(reference.key, candidate),
      });
    }
  });

  // ⚠ Trafienia szukania WYŁĄCZNIE w trybie szukania. Serwer nie szuka przy komendach
  // tworzących, więc bez tego warunku `keepPreviousData` pokazywałoby resztki poprzedniego
  // zapytania jako „pasujące elementy".
  if (command && !isCreation(command.kind) && hits.length > 0) {
    rows.push({ kind: 'heading', key: 'h-hits', label: t('search.results') });

    for (const hit of hits) {
      rows.push({
        kind: 'option',
        key: `hit-${hit.elementId}`,
        onSelect: () => goTo(hit.elementId),
      });
    }
  }

  const selectable = selectableOf(rows);
  const optionKeys = selectable.map((option) => option.key).join('|');

  // ⭐ SEDNO WYMIANY `cmdk`: zbiór pozycji się zmienił, więc zaznaczenie wraca na pierwszą.
  // Tamta biblioteka trzymała zaznaczenie na WARTOŚCI pozycji i po jej zniknięciu zostawała
  // z odwołaniem donikąd — nic nie było zaznaczone, a Enter nie miał czego wykonać.
  useEffect(() => {
    setActiveIndex(0);
  }, [optionKeys]);

  const activeOption = selectable[activeIndex];

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const nextActive = nextIndex(event.key, activeIndex, selectable.length);

    if (nextActive !== null) {
      event.preventDefault();
      setActiveIndex(nextActive);
      return;
    }

    if (event.key === 'Enter' && activeOption && !activeOption.disabled) {
      event.preventDefault();
      activeOption.onSelect();
    }
  }

  return (
    <OmniboxDialog open={isOpen} label={t('search.label')} onClose={close}>

        <div className={styles['field']}>
          {/* Tryb niesie ZNAK KOMENDY, którą się właśnie pisze (`+`, `>`, `!`, `?`) — to część
              DSL-a, więc zostaje tekstem. Bez komendy stoi tu znak marki: ikona, nie glif
              `✦`, który do 2026-08-19 rysowała czcionka systemowa (spec 12B.7). */}
          <span className={styles['prefixHint']} aria-hidden="true">
            {(command ? modeSymbol(command.kind) : '') || <Mark size={16} />}
          </span>

          {/* Wzorzec combobox z WAI-ARIA: ognisko zostaje w polu, a to, która pozycja
              jest aktywna, ogłasza `aria-activedescendant`. Inaczej niż w drzewku
              (`tree` + roving tabindex), gdzie ognisko wędruje po wierszach. */}
          <input
            ref={inputRef}
            className={styles['input']}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t('search.placeholder')}
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls={LIST_ID}
            {...(activeOption
              ? { 'aria-activedescendant': optionDomId(LIST_ID, activeOption.key) }
              : {})}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
          />

          {/* Konstelacja — ta sama, co w wyzwalaczu, więc okno jest „większym
              wyzwalaczem", a nie osobnym bytem. Gaśnie od pierwszej litery: ozdoba
              zaprasza do pustego pola, a przy pisaniu konkurowałaby z komendą. */}
          <OmniboxSky className={`${styles['sky']} ${input === '' ? '' : styles['skyQuiet']}`} />

          {isFetching || isStale ? (
            <span className={styles['spinner']} aria-hidden="true" />
          ) : null}
        </div>

        {command ? (
          <CommandMirror
            command={command}
            ready={command.isComplete && readyToRun}
            typeName={resolvedTypeName(settled)}
          />
        ) : null}

        {error ? (
          <div className={styles['message']} role="alert">
            {describeError(error)}
          </div>
        ) : null}

        <OmniboxList id={LIST_ID} rows={rows} activeIndex={activeIndex} onHover={setActiveIndex}>
          {(row) => renderRow(row, { command, hits, candidateByRow, t })}
        </OmniboxList>

        {/* Nazwa, która nie wskazuje żadnego bytu. Osobno od listy, bo to nie jest
            pozycja do wybrania — to informacja, że komendy nie da się wykonać i dlaczego.
            ⚠ Zdanie zależy od KATALOGU: „nie znalazłem elementu" przy nazwisku brzmiałoby
            jak awaria, a nie jak literówka w nazwie zespołu. */}
        {references.map((reference, index) =>
          settled[index]?.state === 'missing' ? (
            <div key={reference.key} className={styles['message']}>
              {missingMessage(reference, typeDictionary, t)}
            </div>
          ) : null,
        )}

        {input.trim() !== '' && rows.length === 0 && !isFetching && !error ? (
          <div className={styles['message']}>{t('search.noResults')}</div>
        ) : null}

        <div className={styles['footer']}>
          <span>
            <kbd className={styles['kbd']}>
              <ArrowUp size={11} aria-hidden="true" />
              <ArrowDown size={11} aria-hidden="true" />
            </kbd>{' '}
            {t('search.hint.move')}
          </span>
          <span>
            <kbd className={styles['kbd']}>
              <CornerDownLeft size={11} aria-hidden="true" />
            </kbd>{' '}
            {t('search.hint.pick')}
          </span>
          <span>
            <kbd className={styles['kbd']}>esc</kbd> {t('search.hint.close')}
          </span>
        </div>
    </OmniboxDialog>
  );
}

interface RenderContext {
  command: ParsedCommand | undefined;
  hits: readonly ElementSearchHit[];
  candidateByRow: ReadonlyMap<string, ReferenceCandidate>;
  t: (key: string, options: Record<string, unknown>) => string;
}

/**
 * Treść wiersza. Osobno od budowy listy, bo tamta odpowiada za **kolejność i zachowanie**,
 * a ta wyłącznie za to, co widać.
 */
function renderRow(row: OmniboxRow, ctx: RenderContext): ReactNode {
  if (row.kind === 'heading') {
    return null;
  }

  if (row.key.startsWith('prefix-')) {
    const prefix = row.key.slice('prefix-'.length);
    const entry = PREFIXES.find((p) => p.prefix === prefix);

    return (
      <>
        <kbd className={styles['kbd']}>{prefix}</kbd>
        <span>{entry ? ctx.t(`search.kind.${entry.kind}`, {}) : prefix}</span>
      </>
    );
  }

  if (row.key === 'run' && ctx.command) {
    return (
      <>
        <span className={styles['runMark']} aria-hidden="true">
          <CornerDownLeft size={14} />
        </span>
        <span className={styles['itemRun']}>{ctx.t(`search.run.${ctx.command.kind}`, {})}</span>
      </>
    );
  }

  if (row.key.startsWith('pick-')) {
    const candidate = ctx.candidateByRow.get(row.key);

    if (!candidate) {
      return null;
    }

    // Kształt niesie KATALOG, z którego pochodzi kandydat (decyzja 58: kształt koduje rodzaj,
    // kolor zostaje dla temperatury): ■ element · ● byt organizacyjny · ◆ pozycja słownika.
    // Przy właścicielu dochodzi podpis „osoba"/„zespół" — bez niego user i zespół o tej samej
    // nazwie dają dwa nieodróżnialne wiersze. Typy takiego podpisu nie potrzebują, bo słownik
    // jest unikalny po normalizacji (jeden „Serwis", nie dwa).
    return (
      <>
        <span className={styles['hitMark']} aria-hidden="true">
          <CandidateMark candidate={candidate} />
        </span>
        <span className={styles['hitTitle']}>{candidate.title}</span>
        {candidate.dictionary === 'org' ? (
          <span className={styles['hitType']}>
            {ctx.t(`search.owner.${candidate.ownerKind}`, {})}
          </span>
        ) : candidate.dictionary === 'elements' && candidate.type ? (
          <span className={styles['hitType']}>{candidate.type}</span>
        ) : null}
      </>
    );
  }

  const hit = ctx.hits.find((h) => `hit-${h.elementId}` === row.key);

  if (!hit) {
    return null;
  }

  return (
    <>
      <span className={styles['hitMark']} aria-hidden="true">
        <Box size={13} />
      </span>
      <span className={styles['hitTitle']}>{hit.title}</span>
      {/* Rodzic z `has` — bez niego powtarzalne tytuły z różnych modułów wyglądają jak
          duble (2026-08-18). Treść użytkownika, nie etykieta — stąd bez klucza i18n. */}
      {hit.parentTitle ? <span className={styles['hitParent']}>{hit.parentTitle}</span> : null}
      {hit.type ? <span className={styles['hitType']}>{hit.type}</span> : null}
      {/* „Dlaczego to trafienie" — bez tego wiersz dopasowany po opisie wygląda
          na przypadkowy, bo szukanej frazy nie widać w tytule. */}
      {hit.matchedInDescription ? (
        <span className={styles['hitWhy']}>{ctx.t('search.matchedInDescription', {})}</span>
      ) : null}
    </>
  );
}

/**
 * Znak kandydata: element · osoba · zespół · pozycja słownika typów.
 *
 * ⚠ Do 2026-08-19 były to znaki czcionki (`■ ● ◆`) mimo spec 12B.7 — a `●` znaczyło
 * ZARAZEM osobę i zespół, więc kształt niósł mniej, niż mówił o nim komentarz obok.
 * Ikona rozróżnia jedno od drugiego, bo `ownerKind` i tak jest na wierzchu.
 */
function CandidateMark({ candidate }: { candidate: ReferenceCandidate }) {
  const Icon: LucideIcon =
    candidate.dictionary === 'org'
      ? candidate.ownerKind === 'team'
        ? Users
        : User
      : candidate.dictionary === 'types'
        ? Tag
        : Box;

  return <Icon size={13} />;
}

/**
 * Zdanie o nazwie, która nic nie wskazuje — **osobne dla każdego katalogu**.
 *
 * Przy typie są to dwie różne sytuacje i dwa różne wyjścia: typu **nie ma** (trzeba go założyć
 * w ustawieniach albo poprosić architekta) kontra typ jest **wycofany** (istnieje, ale nie
 * wolno go już nadawać — trzeba go przywrócić albo wybrać inny). Wrzucenie obu pod „nie ma
 * takiego typu" wysyłałoby człowieka do zakładania pozycji, która już jest.
 */
function missingMessage(
  reference: Reference,
  typeDictionary: readonly ElementTypeEntry[],
  t: (key: string, options: Record<string, unknown>) => string,
): string {
  if (reference.role === 'owner') {
    return t('search.notFoundOwner', { name: reference.name });
  }

  if (reference.role === 'type') {
    const withdrawn = withdrawnTypeName(reference.name, typeDictionary);

    return withdrawn
      ? t('search.inactiveType', { name: withdrawn })
      : t('search.notFoundType', { name: reference.name });
  }

  return t('search.notFound', { name: reference.name });
}

/**
 * Znak trybu przy polu — ten sam alfabet co prefiksy, żeby dało się je skojarzyć.
 *
 * ⚠ Tryb szukania **nie ma prefiksu** i to nie jest brak do załatania: pusty wynik znaczy
 * „nie ma znaku komendy", a wołający stawia w to miejsce znak marki (wcześniej stał tu glif
 * `✦` rysowany czcionką systemową — spec 12B.7).
 */
function modeSymbol(kind: ParsedCommand['kind']): string {
  return PREFIXES.find((p) => p.kind === kind)?.prefix ?? '';
}
